(() => {
  const SELECTOR = '[data-permanent-product-tabs]';

  class PermanentProductTabs {
    constructor(root) {
      if (root.dataset.permanentTabsReady === 'true') return;
      root.dataset.permanentTabsReady = 'true';
      this.root = root;
      this.buttons = Array.from(root.querySelectorAll('[data-permanent-tab]'));
      this.panels = new Map(Array.from(root.querySelectorAll('[data-permanent-panel]')).map((panel) => [panel.dataset.permanentPanel, panel]));
      if (!this.buttons.length || !this.panels.size) return;

      root.permanentProductTabs = this;
      root.addEventListener('click', (event) => this.onClick(event));
      root.addEventListener('keydown', (event) => this.onKeydown(event));

      const hashTarget = window.location.hash === '#permanent-app-setup' ? 'app' : '';
      const initial = this.panels.has(hashTarget || root.dataset.initialTab) ? (hashTarget || root.dataset.initialTab) : this.buttons[0].dataset.permanentTab;
      root.classList.add('is-ready');
      this.activate(initial);
    }

    activate(target, options = {}) {
      if (!this.panels.has(target)) return false;
      this.buttons.forEach((button) => {
        const active = button.dataset.permanentTab === target;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel, panelTarget) => { panel.hidden = panelTarget !== target; });
      if (options.focusButton) this.buttons.find((button) => button.dataset.permanentTab === target)?.focus();
      if (options.scroll) this.root.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      return true;
    }

    onClick(event) {
      const button = event.target.closest('[data-permanent-tab]');
      if (button && this.root.contains(button)) this.activate(button.dataset.permanentTab);
    }

    onKeydown(event) {
      const button = event.target.closest('[data-permanent-tab]');
      if (!button || !this.root.contains(button)) return;
      const current = this.buttons.indexOf(button);
      let next = current;
      if (event.key === 'ArrowRight') next = (current + 1) % this.buttons.length;
      else if (event.key === 'ArrowLeft') next = (current - 1 + this.buttons.length) % this.buttons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.buttons.length - 1;
      else return;
      event.preventDefault();
      this.activate(this.buttons[next].dataset.permanentTab, { focusButton: true });
    }
  }

  class PermanentInstallGuide {
    constructor(root) {
      if (root.dataset.installGuideReady === 'true') return;
      root.dataset.installGuideReady = 'true';
      this.root = root;
      this.routeInputs = Array.from(root.querySelectorAll('[data-install-route-choice] input[type="radio"]'));
      this.routePanels = Array.from(root.querySelectorAll('[data-install-route-panel]'));
      this.checks = Array.from(root.querySelectorAll('[data-install-step-check]'));
      this.progress = root.querySelector('[data-install-progress-bar]');
      this.progressText = root.querySelector('[data-install-progress-text]');
      this.storageKey = `hlo-install-guide-${root.dataset.productId || 'permanent'}`;
      this.printState = null;

      root.classList.add('is-interactive');
      root.addEventListener('change', (event) => this.onChange(event));
      root.addEventListener('click', (event) => this.onClick(event));
      window.addEventListener('beforeprint', () => this.preparePrint());
      window.addEventListener('afterprint', () => this.restoreAfterPrint());

      this.restoreState();
      const route = this.routeInputs.find((input) => input.checked)?.value || 'garage';
      this.selectRoute(route, false);
      this.updateProgress();
    }

    readState() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      } catch (_error) {
        return {};
      }
    }

    saveState() {
      const state = {
        route: this.routeInputs.find((input) => input.checked)?.value || 'garage',
        completed: this.checks.filter((input) => input.checked).map((input) => input.dataset.installStepCheck)
      };
      try { localStorage.setItem(this.storageKey, JSON.stringify(state)); } catch (_error) { /* Progress persistence is optional. */ }
    }

    restoreState() {
      const state = this.readState();
      if (state.route) {
        const savedRoute = this.routeInputs.find((input) => input.value === state.route);
        if (savedRoute) savedRoute.checked = true;
      }
      const completed = new Set(Array.isArray(state.completed) ? state.completed : []);
      this.checks.forEach((input) => { input.checked = completed.has(input.dataset.installStepCheck); });
    }

    selectRoute(route, persist = true) {
      this.routePanels.forEach((panel) => { panel.hidden = panel.dataset.installRoutePanel !== route; });
      const input = this.routeInputs.find((candidate) => candidate.value === route);
      if (input) input.checked = true;
      if (persist) this.saveState();
    }

    updateProgress() {
      const complete = this.checks.filter((input) => input.checked).length;
      const total = this.checks.length;
      if (this.progress) {
        this.progress.max = total;
        this.progress.value = complete;
        this.progress.setAttribute('aria-valuetext', `${complete} of ${total} installation modules complete`);
      }
      if (this.progressText) {
        this.progressText.textContent = complete === total && total > 0
          ? `All ${total} modules complete — ready for the final walkthrough!`
          : `${complete} of ${total} modules complete`;
      }
    }

    reset() {
      this.checks.forEach((input) => { input.checked = false; });
      this.updateProgress();
      this.saveState();
      const status = document.getElementById('a11y-status');
      if (status) status.textContent = 'Installation checklist reset.';
    }

    onChange(event) {
      const routeInput = event.target.closest('[data-install-route-choice] input[type="radio"]');
      if (routeInput && this.root.contains(routeInput)) {
        this.selectRoute(routeInput.value);
        return;
      }
      if (event.target.matches('[data-install-step-check]')) {
        this.updateProgress();
        this.saveState();
      }
    }

    onClick(event) {
      const printButton = event.target.closest('[data-install-print]');
      if (printButton && this.root.contains(printButton)) {
        this.preparePrint();
        requestAnimationFrame(() => window.print());
        return;
      }
      const resetButton = event.target.closest('[data-install-reset]');
      if (resetButton && this.root.contains(resetButton)) this.reset();
    }

    preparePrint() {
      if (this.printState) return;
      const details = Array.from(this.root.querySelectorAll('details'));
      this.printState = {
        details: details.map((item) => item.open),
        panels: this.routePanels.map((panel) => panel.hidden)
      };
      details.forEach((item) => { item.open = true; });
      this.routePanels.forEach((panel) => { panel.hidden = false; });
    }

    restoreAfterPrint() {
      if (!this.printState) return;
      const details = Array.from(this.root.querySelectorAll('details'));
      details.forEach((item, index) => { item.open = Boolean(this.printState.details[index]); });
      this.routePanels.forEach((panel, index) => { panel.hidden = Boolean(this.printState.panels[index]); });
      this.printState = null;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll?.(SELECTOR).forEach((root) => new PermanentProductTabs(root));
    scope.querySelectorAll?.('[data-permanent-install-guide]').forEach((root) => new PermanentInstallGuide(root));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-permanent-open-tab]');
    if (!trigger) return;
    const tabs = document.querySelector(SELECTOR);
    if (!tabs?.permanentProductTabs?.activate(trigger.dataset.permanentOpenTab, { scroll: true })) return;
    event.preventDefault();
  });
})();
